// The four pictures on the "How TempDash works" sheet (worker
// components/HowItWorksSheet.jsx), drawn in the house art style of
// shared/announcementIcons.js: flat, rounded, no outlines, each object lifted off
// the page by a `depth` layer offset down and right. 72 by 72, decorative (the
// card carries the words).
//
// ⛔ Colours are {{slot}}s filled from shared/pastelTones.js only: never
// turquoise (the logo) or the link blue (actions). Fill with fillIcon from
// shared/announcementIcons.js.

export const lift = (shape, fill, dx = 3, dy = 3) => `<g transform="translate(${dx} ${dy})">${shape('{{depth}}')}</g>${shape(fill)}`
export const path = (d) => (fill) => `<path d="${d}" fill="${fill}"/>`
export const line = (d, stroke, w = 3) => `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`
export const dot = (x, y, r, fill) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`
export const rect = (x, y, w, h, r, fill, o = 1) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${fill}"${o < 1 ? ` fill-opacity="${o}"` : ""}/>`
export function sparkle(x, y, r, fill) {
  const k = r * 0.18
  return `<path d="M${x} ${y - r}Q${x + k} ${y - k} ${x + r} ${y}Q${x + k} ${y + k} ${x} ${y + r}Q${x - k} ${y + k} ${x - r} ${y}Q${x - k} ${y - k} ${x} ${y - r}Z" fill="${fill}"/>`
}

const ART = {
  // A lavender building with a yellow door, a coral pin dropped on it: you're here.
  clockIn: lift((f) => rect(8, 26, 40, 36, 5, f), '{{lavender}}')
    + rect(14, 33, 8, 7, 2, '{{white}}') + rect(34, 33, 8, 7, 2, '{{white}}')
    + rect(22, 46, 12, 16, 3, '{{yellow}}') + dot(31, 54, 1.4, '{{ink}}')
    + `<ellipse cx="53" cy="60" rx="9" ry="3" fill="{{mint}}"/>`
    + lift(path('M53 14c7 0 12 5 12 12 0 8-8 14-12 20-4-6-12-12-12-20 0-7 5-12 12-12z'), '{{coral}}', 2, 2)
    + dot(53, 26, 4.5, '{{white}}')
    + sparkle(16, 13, 6, '{{yellow}}'),

  // A yellow clock reading home time, a pink moon: the day's done.
  clockOut: lift((f) => dot(32, 38, 22, f), '{{yellow}}')
    + dot(32, 38, 16, '{{white}}')
    + dot(32, 25, 1.8, '{{coral}}') + dot(45, 38, 1.8, '{{coral}}') + dot(32, 51, 1.8, '{{coral}}') + dot(19, 38, 1.8, '{{coral}}')
    + line('M32 38V29M32 38l7 4', '{{ink}}', 3) + dot(32, 38, 2.4, '{{ink}}')
    + lift(path('M60 8a11 11 0 1 0 6 19 9 9 0 0 1-6-19z'), '{{softPink}}', 2, 2)
    + sparkle(62, 50, 5, '{{lavender}}'),

  // A pink speech bubble with a white question mark, a mint one behind: we'll sort it.
  help: lift(path('M38 12h22a5 5 0 0 1 5 5v14a5 5 0 0 1-5 5h-3l1 7-8-7h-12a5 5 0 0 1-5-5V17a5 5 0 0 1 5-5z'), '{{mint}}', 2, 2)
    + dot(44, 24, 1.8, '{{white}}') + dot(50, 24, 1.8, '{{white}}') + dot(56, 24, 1.8, '{{white}}')
    + lift(path('M12 24h30a6 6 0 0 1 6 6v18a6 6 0 0 1-6 6H24l-10 8 1-8h-3a6 6 0 0 1-6-6V30a6 6 0 0 1 6-6z'), '{{pink}}')
    + line('M22 34c0-4 3-6 5-6s5 2 5 5c0 4-5 4-5 8', '{{white}}', 3.6) + dot(27, 47, 2.2, '{{white}}')
    + sparkle(62, 54, 6, '{{yellow}}'),

  // A mint banknote with yellow coins in front, a pink sparkle: paid.
  paid: lift((f) => rect(8, 14, 44, 28, 5, f), '{{mint}}')
    + dot(30, 28, 7, '{{white}}')
    + `<path d="M32.5 24.5c-.6-1-1.6-1.5-2.7-1.5-1.7 0-2.8 1.2-2.8 2.9V32m-2-4h5.5M25 32h8" fill="none" stroke="{{green}}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`
    + rect(13, 19, 6, 3, 1.5, '{{white}}') + rect(41, 34, 6, 3, 1.5, '{{white}}')
    + lift((f) => `<ellipse cx="46" cy="58" rx="14" ry="6" fill="${f}"/>`, '{{yellow}}', 2, 2)
    + `<ellipse cx="46" cy="52" rx="14" ry="6" fill="{{coral}}"/>`
    + `<ellipse cx="46" cy="47" rx="14" ry="6" fill="{{yellow}}"/>`
    + `<ellipse cx="46" cy="47" rx="8" ry="3" fill="{{coral}}" fill-opacity="0.45"/>`
    + sparkle(62, 12, 7, '{{pink}}') + sparkle(14, 54, 5, '{{lavender}}'),
}

export const HOW_IT_WORKS_ART_XML = Object.fromEntries(Object.entries(ART).map(
  ([k, body]) => [k, `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">${body}</svg>`],
))

// The cards, in order. Bump HOW_IT_WORKS_VERSION whenever a line changes: the
// server records which version each worker agreed to (claim evidence).
// v2: founder copy 2026-10-01 (outcomes only, no app mechanics).
// v3: Get paid says approval then wallet, no timer (2026-10-14, #3725 wording).
export const HOW_IT_WORKS_VERSION = 3
//
// Other languages: this file stays import free (the website's prerender and node
// read it), so the apps hand their words in (shared/engineWords.js, from
// shared/i18n/messages/howItWorksArt). Each title and body is read when shown.
let words = null
export function setHowItWorksWords(fn) { words = typeof fn === 'function' ? fn : null }
function tr() {
  try {
    const t = words ? words() : null
    return t && t.lang && t.lang !== 'en' ? t : null
  } catch {
    return null
  }
}
const card = (art, title, body) => ({
  art,
  get title() { const t = tr(); return t ? t(`cards.${art}.title`) : title },
  get body() { const t = tr(); return t ? t(`cards.${art}.body`) : body },
})
export const HOW_IT_WORKS_CARDS = [
  card('clockIn', 'Check in when you arrive', 'Slide to check in at the workplace.'), // i18n-exempt: English engine (the website and node read it); apps read shared/i18n/messages/howItWorksArt
  card('clockOut', 'Check out when you leave', "When you check out, we'll work out your timesheet automatically."), // i18n-exempt: English engine (the website and node read it); apps read shared/i18n/messages/howItWorksArt
  card('help', 'Something not right?', "Let us know on the shift and we'll get you the right help."), // i18n-exempt: English engine (the website and node read it); apps read shared/i18n/messages/howItWorksArt
  card('paid', 'Get paid', "Once your hours are approved, your pay lands in your TempDash Wallet."), // i18n-exempt: English engine (the website and node read it); apps read shared/i18n/messages/howItWorksArt
]
